import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { Button, Card, Heading, InlineNotice, Separator, toast } from '@wit-support/ui';
import { env } from '../env.js';
import { fetchMe } from '../lib/api.js';
import {
  CONNECTOR_CONSENT_NOTE,
  CONSENT_NOTICE,
  describeScopes,
  mcpConnectorsFor,
  oauthRedirectTarget,
  signedOAuthQuery,
} from '../lib/oauth-flow.js';
import { LoginPage } from './login.js';

/**
 * Pages of the MCP OAuth flow (spec 2026-10-12 agent-data-mcp, D4). An AI
 * client (Claude, ChatGPT, Cursor, …) sends the operator to the authorization
 * server in admin-api, which redirects here with a signed copy of the request:
 *
 *   /oauth/login   — not logged in yet: the ordinary admin login form, posting
 *                    the signed request along so the server resumes it.
 *   /oauth/consent — logged in: what the client asks for, Allow / Deny.
 *
 * Rendered outside the app router and `MeGate` (see `App`): both pages leave
 * with a full-page navigation to wherever the server says.
 */

const AGENT_DATA_PERMISSION = 'agent_data.read';

function ExpiredRequest() {
  return (
    <Shell title="Запрос на подключение устарел">
      <p className="text-sm text-[var(--color-text-muted)]">
        Ссылка неполная или истекла. Вернитесь в приложение и начните подключение заново.
      </p>
    </Shell>
  );
}

function Shell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="min-h-dvh grid place-items-center bg-[var(--color-surface-canvas)] text-[var(--color-text-primary)] p-6">
      <Card className="w-full max-w-[480px] p-8">
        <Heading level={1} size="md" className="mb-4">
          {title}
        </Heading>
        {children}
      </Card>
    </div>
  );
}

export function OAuthLoginPage() {
  const oauthQuery = signedOAuthQuery(window.location.search);
  if (!oauthQuery) return <ExpiredRequest />;
  return (
    <LoginPage
      oauthQuery={oauthQuery}
      subtitle="Войдите, чтобы подключить нейросеть к данным WIT. Доступ — только у владельцев, супервайзеров и маркетологов."
    />
  );
}

interface PublicClient {
  client_name?: string;
  client_uri?: string;
}

async function fetchPublicClient(clientId: string): Promise<PublicClient | null> {
  const r = await fetch(
    `${env.VITE_ADMIN_API_URL}/auth/oauth2/public-client?client_id=${encodeURIComponent(clientId)}`,
    { credentials: 'include' },
  );
  if (!r.ok) return null;
  return (await r.json()) as PublicClient;
}

const hostOf = (uri: string | null): string | null => {
  if (!uri) return null;
  try {
    return new URL(uri).host;
  } catch {
    return null;
  }
};

export function OAuthConsentPage() {
  const oauthQuery = signedOAuthQuery(window.location.search);
  const params = new URLSearchParams(oauthQuery ?? '');
  const clientId = params.get('client_id');
  const [pending, setPending] = useState(false);

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => fetchMe(env.VITE_ADMIN_API_URL),
    retry: 1,
    enabled: Boolean(oauthQuery),
  });
  const client = useQuery({
    queryKey: ['oauth-public-client', clientId],
    queryFn: () => fetchPublicClient(clientId!),
    enabled: Boolean(oauthQuery && clientId && me.data),
    retry: false,
  });

  if (!oauthQuery || !clientId) return <ExpiredRequest />;
  if (me.isPending) return null;
  if (me.isError || !me.data) {
    return (
      <Shell title="Нужно войти заново">
        <p className="text-sm text-[var(--color-text-muted)]">
          Сессия админки закончилась. Вернитесь в приложение и начните подключение заново.
        </p>
      </Shell>
    );
  }

  const allowed = me.data.permissions.includes(AGENT_DATA_PERMISSION);
  const clientName = client.data?.client_name || clientId;
  const redirectHost = hostOf(params.get('redirect_uri'));
  const scopes = describeScopes(params.get('scope'));
  // MCP module the token is for (spec builder-v4 §16.4 п. 5); null ⇒ not shown.
  const connectors = mcpConnectorsFor(params.getAll('resource'));
  const connector = connectors[0] ?? null;
  const connectorWarning = connectors.find((c) => c.warning)?.warning;

  async function decide(accept: boolean) {
    setPending(true);
    try {
      const r = await fetch(`${env.VITE_ADMIN_API_URL}/auth/oauth2/consent`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ accept, oauth_query: oauthQuery }),
      });
      const body: unknown = await r.json().catch(() => null);
      const target = r.ok ? oauthRedirectTarget(body) : null;
      if (target) {
        window.location.assign(target);
        return;
      }
      const b = body as { error_description?: string; message?: string } | null;
      toast.error(
        b?.error_description ?? b?.message ?? `Не удалось завершить подключение (${r.status})`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? `Ошибка сети: ${e.message}` : 'Ошибка сети');
    } finally {
      setPending(false);
    }
  }

  return (
    <Shell title={`Подключить «${clientName}»?`}>
      <p className="text-sm text-[var(--color-text-muted)]">
        Вы вошли как <span className="text-[var(--color-text-primary)]">{me.data.admin.email}</span>
        .
        {redirectHost ? (
          <>
            {' '}
            Доступ получит приложение по адресу{' '}
            <span className="text-[var(--color-text-primary)]">{redirectHost}</span>.
          </>
        ) : null}
      </p>
      {connectors.length > 0 ? (
        <>
          <p className="mt-4 text-sm">
            {connectors.length === 1 ? 'Коннектор ' : 'Коннекторы '}
            {connectors.map((c, i) => (
              <span key={c.module}>
                {i > 0 ? ' и ' : ''}
                <span className="font-medium text-[var(--color-text-primary)]">«{c.name}»</span>
              </span>
            ))}
            {connectors.length === 1 ? ` — ${connectors[0]!.summary}` : '.'}
          </p>
          {connectorWarning ? (
            <InlineNotice
              variant="warning"
              bordered
              icon={TriangleAlert}
              className="mt-2 p-3 text-sm font-medium"
            >
              {connectorWarning}
            </InlineNotice>
          ) : null}
        </>
      ) : null}
      {scopes.length > 0 ? (
        <ul className="mt-4 space-y-2 text-sm">
          {scopes.map((s) => (
            <li key={s.scope}>
              • {s.label}
              {/* The connector's own money warning already says it — once is enough. */}
              {s.warning && !connectorWarning ? (
                <InlineNotice
                  variant="warning"
                  bordered
                  icon={TriangleAlert}
                  className="mt-2 p-3 text-sm font-medium"
                >
                  {s.warning}
                </InlineNotice>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      <InlineNotice variant="info" bordered className="mt-4 p-3 text-sm">
        {CONSENT_NOTICE}
        {connector ? ` ${CONNECTOR_CONSENT_NOTE}` : null}
      </InlineNotice>
      {!allowed ? (
        <InlineNotice variant="danger" bordered className="mt-4 p-3 text-sm">
          У вашей роли нет доступа к данным для нейросети — он есть у владельцев, супервайзеров и
          маркетологов.
        </InlineNotice>
      ) : null}
      <Separator className="my-6" />
      <div className="flex justify-end gap-3">
        <Button variant="secondary" disabled={pending} onClick={() => void decide(false)}>
          Отклонить
        </Button>
        <Button disabled={pending || !allowed} onClick={() => void decide(true)}>
          Разрешить
        </Button>
      </div>
    </Shell>
  );
}
